import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../components/Card';
import { Pill } from '../components/Pill';
import { adminApi } from '../api/admin';
import { fmtUSD } from '../lib/format';

export function ActivityScreen() {
  const [limit, setLimit] = useState(50);
  const events = useQuery({ queryKey: ['admin', 'activity', limit], queryFn: () => adminApi.activity(limit) });
  const rows = events.data ?? [];

  return (
    <div style={{ padding: '24px 28px 60px', display: 'grid', gap: 18, maxWidth: 1000 }}>
      <Card padding={0}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 16 }}>Global activity feed</div>
            <div style={{ color: 'var(--ink-3)', fontSize: 12, marginTop: 2 }}>Every event across all creators — newest first</div>
          </div>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={{
              background: 'var(--bg-elev)',
              border: '1px solid var(--line-strong)',
              borderRadius: 'var(--r-md)',
              padding: '6px 10px',
              fontSize: 12,
              color: 'var(--ink)',
            }}
          >
            <option value={25}>Last 25</option>
            <option value={50}>Last 50</option>
            <option value={100}>Last 100</option>
            <option value={200}>Last 200</option>
          </select>
        </div>

        {rows.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
            {events.isLoading ? 'Loading…' : 'No events yet.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {rows.map((a, i) => (
              <div
                key={a.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '14px 20px',
                  borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--line)',
                }}
              >
                <EventBadge type={a.eventType} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 500 }}>
                    {humanEvent(a.eventType)}{' '}
                    <span style={{ color: 'var(--ink-3)' }}>
                      — {a.creator?.name ?? '–'}
                      {a.creator && <span style={{ fontFamily: 'var(--font-mono)' }}> · @{a.creator.handle}</span>}
                    </span>
                  </div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-3)', display: 'flex', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
                    {a.code && <span className="mono">code · {a.code}</span>}
                    {a.plan && <span>plan · {a.plan}</span>}
                    <span>{new Date(a.occurredAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                  </div>
                </div>
                {a.amountCents != null && (
                  <div className="num" style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--good)' }}>
                    +{fmtUSD(a.amountCents)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function EventBadge({ type }: { type: string }) {
  const map: Record<string, { tone: 'good' | 'warn' | 'bad' | 'neutral' | 'brand'; label: string }> = {
    new_sub: { tone: 'good', label: 'NEW' },
    renewal: { tone: 'good', label: 'REN' },
    churn: { tone: 'bad', label: 'CHURN' },
    code_approved: { tone: 'brand', label: 'OK' },
    code_rejected: { tone: 'bad', label: 'NO' },
    payout_paid: { tone: 'good', label: '$$' },
  };
  const m = map[type] ?? { tone: 'neutral', label: '?' };
  return <Pill tone={m.tone} size="sm" style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, minWidth: 44, justifyContent: 'center' }}>{m.label}</Pill>;
}

function humanEvent(t: string): string {
  return {
    new_sub: 'New subscriber',
    renewal: 'Subscription renewed',
    churn: 'Subscriber churned',
    code_approved: 'Side code approved',
    code_rejected: 'Side code rejected',
    payout_paid: 'Payout sent',
  }[t] ?? t;
}
